import { Component, Input, OnInit, ViewEncapsulation, ChangeDetectionStrategy, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ExplainerDialogService } from 'app/core/explainer-dialog.service';
import { CustomerStatistics } from 'app/shared/services/analytics-models';
import { DateService } from 'app/shared/services/date-service';
import {
  AiChatUsageSummary,
  SubscriptionDetails,
  LimitationsWithUsage,
  Limitations,
  TierLimitations,
} from 'app/subscription/subscription.model';
import { SubscriptionService } from 'app/subscription/subscription.service';
import { AccountSettingsUpgradeDialogComponent } from '../dialog/account-settings-upgrade-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FlyoutDataType, FlyoutOpeningData } from 'app/flyout/flyout-models';
import { FlyoutService } from 'app/flyout/flyout.service';
import { CustomerModel } from 'app/account-settings/models/customer.model';
import { Tier } from 'app/shared/account/tier.model';
import { UpgradeAccountFlyoutResponse } from 'app/campaign/widgets.model';
import { SubscriptionHandlerBaseComponent } from 'app/core/subscription-handler.base';
import { Currency } from '../../../../shared/account/Currency';
import { catchError, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { EmailTemplateService } from 'app/email-templates/email-template.service';
import { SettingsService } from 'app/shared/services/admin-settings.service';

@Component({
  selector: 'app-account-settings-usage',
  templateUrl: './account-settings-usage.component.html',
  styleUrls: ['./account-settings-usage.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountSettingsUsageComponent extends SubscriptionHandlerBaseComponent implements OnInit {
  public limitationsWithUsage: LimitationsWithUsage;

  @Input() public customer: CustomerModel;
  @Input() public customerLimitations: Limitations;
  @Input() public isSelfServiceCustomer: boolean;
  @Input() public statistics: CustomerStatistics;
  @Input() public currentAccountSiteId?: number;
  @Input() public resetDayInMonth: number;
  @Input() public subscription: SubscriptionDetails;
  @Input() public currency: Currency;
  @Input() public isPublicApiActive: boolean = false;

  public isBackofficePage: boolean;
  public hasSeveralAccounts: boolean;
  public daysUntilReset: number;
  private totalTrafficLimit: number;
  public trafficLeft: number;
  public trafficProgress: number;
  public accountsLimit: number;
  public surveyResponsesLimit: number;
  public isCustomPlan: boolean;
  public Tier = Tier;
  public totalSurveyResponses: number;
  public isSurveyResponsesUnlimited: boolean;
  public emailsCount: number | undefined;
  public publicApiCallsLimit: number;
  public isPublicApiCallsUnlimited: boolean;
  public emailsLimit: number;
  public hasEmailTemplates: boolean = false;
  public publicApiCallsCount: number | undefined;
  public aiUsage: AiChatUsageSummary | null = null;

  private readonly settingsService = inject(SettingsService);

  public EXPLAINER_TEXTS: any;

  constructor(
    private readonly subscriptionService: SubscriptionService,
    private readonly dateService: DateService,
    private readonly translateService: TranslateService,
    private readonly dialog: MatDialog,
    public readonly explainerDialogService: ExplainerDialogService,
    private readonly flyoutService: FlyoutService,
    private readonly emailTemplateService: EmailTemplateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscriptionService.getTierLimitations(this.customer.tier).subscribe((tierLimitations) => {
      this.EXPLAINER_TEXTS = {
        pageviews: {
          header: 'billing.pageviews-title',
          description: 'billing.pageviews-info',
        },
        surveyResponses: {
          header: 'billing.survey-responses-title',
          descriptionKey: this.translateService.instant('explainer.billing.survey-responses-info', {
            defaultSurveyResponsesLimit: tierLimitations.defaultSurveyResponses,
          }),
        },
        automations: {
          header: 'billing.automations-title',
          description: 'billing.automations-info',
        },
        rows: {
          header: 'billing.rows-title',
          description: 'billing.rows-info',
        },
        users: {
          header: 'billing.users-title',
          description: 'billing.users-info',
        },
        profiles: {
          header: 'billing.profiles-title',
          description: 'billing.profiles-info',
        },
        transactionalEmails: {
          header: 'billing.transactional-emails-title',
          description: 'billing.transactional-emails-info',
        },
        publicApiCalls: {
          header: 'billing.public-api-calls-title',
          description: 'billing.public-api-calls-info',
        },
        aiUsage: {
          header: 'billing.ai-usage-title',
          description: 'billing.ai-usage-info',
        },
      };
      this.isBackofficePage = this.currentAccountSiteId === undefined;
      this.hasSeveralAccounts = this.customer.siteIds.length > 1;
      if (this.customerLimitations) {
        this.surveyResponsesLimit = this.customerLimitations.surveyResponsesPerMonth;
        this.totalSurveyResponses = this.subscriptionService.getTotalSurveyResponses(
          this.surveyResponsesLimit,
          tierLimitations.defaultSurveyResponses,
        );
        this.isSurveyResponsesUnlimited = this.subscriptionService.isSurveyResponsesUnlimited(
          this.surveyResponsesLimit,
        );
        this.limitationsWithUsage = new LimitationsWithUsage(this.customerLimitations);
        this.daysUntilReset = this.dateService.calculateDaysUntilReset(this.resetDayInMonth);
        this.limitationsWithUsage.automations.value = this.statistics.automations;
        this.limitationsWithUsage.batchRecords.value = this.statistics.importExportRows;
        this.limitationsWithUsage.batchRecordsPerMonth.value = this.customerLimitations.importExportRowsPerMonth;
        this.limitationsWithUsage.persons.value = this.statistics.uniqueProfiles;
        this.limitationsWithUsage.users.value = this.statistics.users;
        this.limitationsWithUsage.surveyResponses.value = this.statistics.surveyResponses;
        this.limitationsWithUsage.events.value = this.statistics.pageviews;
        this.limitationsWithUsage.events.max = this.customerLimitations.eventsPerMonth;
        this.initializeData(this.customerLimitations, tierLimitations);
      }
      this.calculteResetDate();
      this.emailsLimit = this.customerLimitations?.emailsPerMonth || 1000;
      if (this.customer?.id) {
        const startDate = this.subscriptionService.getStartDate(this.resetDayInMonth);
        const endDate = this.dateService.nowLocal();
        const emailCount$ = this.isBackofficePage
          ? this.subscriptionService.getEmailCountAdmin(this.customer.id, startDate, endDate)
          : this.subscriptionService.getEmailCount(this.customer.id, startDate, endDate);
        super.addSubscription(
          emailCount$.pipe(catchError(() => of(null))).subscribe((count) => {
            this.emailsCount = count;
          }),
        );
        super.addSubscription(
          this.emailTemplateService
            .getByCustomer(this.customer.id)
            .pipe(catchError(() => of([])))
            .subscribe((templates) => {
              this.hasEmailTemplates = templates.length > 0;
            }),
        );
        if (this.isPublicApiActive) {
          // One call gives both the count and the effective limit, so the page can never show a
          // different ceiling than the one the gateway enforces.
          const publicApiCallsUsage$ = this.isBackofficePage
            ? this.subscriptionService.getPublicApiCallsUsageAdmin(this.customer.id)
            : this.subscriptionService.getPublicApiCallsUsage(this.customer.id);
          super.addSubscription(
            publicApiCallsUsage$.pipe(catchError(() => of(null))).subscribe((usage) => {
              this.publicApiCallsCount = usage?.used;
              this.publicApiCallsLimit = usage?.limit;
              this.isPublicApiCallsUnlimited = this.subscriptionService.isUnlimited(this.publicApiCallsLimit);
            }),
          );
        }
        if (!this.isBackofficePage) {
          super.addSubscription(
            this.settingsService.settings$
              .pipe(
                map((settings) => !!settings?.adminToggle),
                distinctUntilChanged(),
                switchMap((isAdminView) =>
                  isAdminView ? this.subscriptionService.getAiChatUsage().pipe(catchError(() => of(null))) : of(null),
                ),
              )
              .subscribe((usage) => {
                this.aiUsage = usage;
              }),
          );
        }
      }
      super.addSubscription(
        this.flyoutService.flyoutUpgradedAccountBS.subscribe((result: UpgradeAccountFlyoutResponse) => {
          if (result) {
            this.limitationsWithUsage.events.max = result.eventsPerMonth;
            this.initializeData(result, tierLimitations);
          }
        }),
      );
    });
  }

  private initializeData(customerLimitations, tierLimitations: TierLimitations) {
    if (this.isSelfServiceCustomer) {
      this.isCustomPlan = this.subscriptionService.isCustomPlan(customerLimitations, tierLimitations);
      const limitationsCache = this.isBackofficePage
        ? undefined
        : this.subscriptionService.getLimitationsFromSessionStorage('limitations', this.currentAccountSiteId);
      if (limitationsCache) {
        this.totalTrafficLimit = limitationsCache.eventsPerMonth;
        this.accountsLimit = limitationsCache.accounts;
      } else {
        this.totalTrafficLimit = customerLimitations.eventsPerMonth;
        this.accountsLimit = customerLimitations.accounts;
      }
      this.calculateTrafficLeft();
    } else {
      this.accountsLimit = customerLimitations.accounts;
      this.totalTrafficLimit = this.limitationsWithUsage.events.max;
    }
  }

  public openUpgradeAccountFlyout() {
    this.flyoutService.open({
      subscriptionExists: this.subscription !== undefined,
      width: 576,
      type: FlyoutDataType.UpgradeAccount,
      subscriptionDetails: this.subscription,
      subscriptionCurrency: this.currency,
    } as FlyoutOpeningData);
  }

  public calculteResetDate(): string {
    return this.dateService.getDateTimeNowFormatted(this.daysUntilReset);
  }

  private calculateTrafficLeft() {
    const value = this.limitationsWithUsage?.events?.value;
    if (value !== undefined && this.totalTrafficLimit !== undefined) {
      if (this.totalTrafficLimit <= this.limitationsWithUsage.events.value) {
        this.trafficProgress = 100;
        this.trafficLeft = 0;
      } else {
        const trafficLeftCalc = this.totalTrafficLimit - value;
        this.trafficLeft = trafficLeftCalc;
        this.trafficProgress = Math.round((value / this.totalTrafficLimit) * 100);
      }
    }
  }

  public openUpgradePopup() {
    this.dialog.open(AccountSettingsUpgradeDialogComponent);
  }
}
